/**
 * Finishing the word somebody is still typing, against a dictionary of words that really occur
 * in food names (#5319).
 *
 * THE PROBLEM THIS SOLVES. The last word of a search is usually unfinished — "choc" is not a
 * word, "chocolate" is. The server's Elasticsearch query has no prefix matching of any kind, so
 * it cannot answer a fragment at all. The app used to hide that by letting the last typed word
 * match as a BEGINNING of a food's word, a rule the server does not have, which meant the two
 * halves of one result list judged the same text by different rules.
 *
 * Completing the fragment into whole words first means neither side ever sees a fragment. The
 * matcher then compares whole words, which is the one rule both sides share.
 *
 * WHY IT EXPANDS TO A SET RATHER THAN PICKING ONE WORD. Guessing a single completion would be
 * wrong often enough to need an "showing results for X" escape hatch, and a wrong silent guess
 * is worse than no guess. Expanding to the few most common completions instead keeps every
 * plausible reading alive: "yog" becomes both "yogurt" and "yoghurt", which no single guess
 * could do, because those two are spelled differently AND stem differently.
 *
 * WHY THE TYPED WORD IS ALWAYS KEPT. The dictionary powers completion only, never search. A
 * word it does not hold — a new brand, or a typo the fuzzy matching will handle — must still be
 * searched exactly as typed, or the dictionary's size would decide which foods are findable.
 * That is the rule that makes it safe to cut the dictionary down aggressively.
 *
 * Everything is passed in rather than imported, so this module loads under the node test runner
 * and its test drives the real code — the same convention as `food-search-match.ts`.
 */

import { allowedEdits } from './food-search-match';

/**
 * How many completions one unfinished word may turn into.
 *
 * Kept small on purpose. Each extra completion widens the result list, and because the words
 * are held most-frequent-first, the ones past the first few are rare enough that they cost more
 * in noise than they add in reach.
 */
export const MAX_WORD_COMPLETIONS = 5;

/** A dictionary of food-name words, ready to complete a part-typed word against. */
export interface FoodWordDictionary {
  /**
   * The most common whole words starting with `prefix`, most common first, at most `maxWords`.
   * The prefix itself is included when it is a dictionary word in its own right.
   */
  completionsFor(prefix: string, maxWords: number): string[];
  /** How many words the dictionary holds. For diagnostics and tests. */
  readonly size: number;
}

/**
 * Could `typed` be a mistyped beginning of `word`, allowing at most `maxEdits` corrections?
 *
 * This is what lets "broco" reach "broccoli". Plain beginning-matching cannot: the letters stop
 * agreeing at the fifth one, so "broco" is not a beginning of "broccoli" at all, however obvious
 * the intention is to a person.
 *
 * It measures the distance from `typed` to the CLOSEST BEGINNING of `word`, not to the whole of
 * it — the user has not finished typing, so the rest of the word must cost nothing. "broco" is
 * one correction away from "brocc", which is a beginning of "broccoli", so the answer is one
 * edit, not the four it would take to reach "broccoli" in full.
 *
 * Only beginnings up to `typed.length + maxEdits` letters long are worth measuring: a longer one
 * needs more insertions than the budget allows whatever its letters are.
 */
export function isMistypedBeginningOf(typed: string, word: string, maxEdits: number): boolean {
  const longestWorthTrying = Math.min(word.length, typed.length + maxEdits);
  // distance from an empty typed string to each beginning of `word` is the number of letters in
  // that beginning, since each one has to be inserted
  let twoRowsBack: number[] | undefined = undefined;
  let previousRow: number[] = Array.from({ length: longestWorthTrying + 1 }, (_, j) => j);
  for (let i = 1; i <= typed.length; i++) {
    const currentRow = [i];
    let bestInRow = i;
    for (let j = 1; j <= longestWorthTrying; j++) {
      const substitution = previousRow[j - 1] + (typed[i - 1] === word[j - 1] ? 0 : 1);
      let cost = Math.min(previousRow[j] + 1, currentRow[j - 1] + 1, substitution);
      // two letters typed the wrong way round count as ONE edit, not two -- the matcher counts
      // them that way because Elasticsearch does, and completion has to agree with it or "mlik"
      // finds Milk when searching and cannot be completed to it
      if (i > 1 && j > 1 && twoRowsBack && typed[i - 1] === word[j - 2] && typed[i - 2] === word[j - 1]) {
        cost = Math.min(cost, twoRowsBack[j - 2] + 1);
      }
      currentRow.push(cost);
      bestInRow = Math.min(bestInRow, cost);
    }
    // every later row can only add to the best figure in this one, so once a whole row is over
    // budget the answer is known
    if (bestInRow > maxEdits) {
      return false;
    }
    twoRowsBack = previousRow;
    previousRow = currentRow;
  }
  // the answer is the closest ANY beginning of the word came, not the whole word's distance
  return Math.min(...previousRow) <= maxEdits;
}

/**
 * How many single-letter edits turn `a` into the WHOLE of `b` — no free ride for an unfinished
 * ending, unlike `isMistypedBeginningOf`.
 *
 * The two are needed for genuinely different questions, and using the wrong one brings back the
 * bug #5114 fixed. "chai" is one edit from "chi", a BEGINNING of "chicken", but four edits from
 * "chicken" itself. So a word somebody has finished typing is judged whole-to-whole, and only a
 * fragment they are still typing gets the beginning treatment.
 */
export function wholeWordDistance(a: string, b: string): number {
  let twoRowsBack: number[] | undefined = undefined;
  let previousRow = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const currentRow = [i];
    for (let j = 1; j <= b.length; j++) {
      const substitution = previousRow[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1);
      let cost = Math.min(previousRow[j] + 1, currentRow[j - 1] + 1, substitution);
      if (i > 1 && j > 1 && twoRowsBack && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        cost = Math.min(cost, twoRowsBack[j - 2] + 1);
      }
      currentRow.push(cost);
    }
    twoRowsBack = previousRow;
    previousRow = currentRow;
  }
  return previousRow[b.length];
}

/** How many letters two words share at the start. Used to rank one correction above another. */
function sharedBeginningLength(a: string, b: string): number {
  let shared = 0;
  while (shared < a.length && shared < b.length && a[shared] === b[shared]) {
    shared++;
  }
  return shared;
}

/**
 * How many corrections may be offered at once.
 *
 * Deliberately fewer than the completions themselves. A correction is a guess about what the
 * user MEANT, where a completion only finishes what they wrote, so corrections must never crowd
 * real completions out of the short list a person actually reads.
 */
const MAX_CORRECTIONS = 2;

/**
 * How much commoner a candidate must be before a word that is ALREADY a real food word is
 * corrected to it. "chiken" is a real enough word to be in the dictionary, but "chicken" is
 * hundreds of times commoner, and that gap is what marks the first as a misspelling.
 */
const CORRECTION_RANK_ADVANTAGE = 10;

/**
 * Build a dictionary from words already ordered MOST COMMON FIRST.
 *
 * That order is the whole ranking — it is why the shipped dictionary stores no counts. A caller
 * merging several sources (the shipped word list and the words of the foods on this device)
 * concatenates them in the order it wants them offered; the first appearance of a repeated word
 * is the one that counts, so whichever source is listed first wins the tie.
 *
 * Words are bucketed by first letter so that completing a prefix only ever scans the words
 * beginning with that letter, rather than the whole dictionary, on every keystroke. That also
 * bounds the correction of a mistyped word below: a typo in the FIRST letter is not found,
 * which is the deliberate price of not measuring every word in the dictionary on each
 * keystroke. The first letter is also the one the user is least likely to get wrong, since it
 * is on screen from the very first keypress.
 */
export function createFoodWordDictionary(rankedWords: string[]): FoodWordDictionary {
  // a word's POSITION is how common it is -- the list arrives most-common-first, which is why
  // no counts are shipped. Both rules below compare positions rather than counts.
  const positionOf = new Map<string, number>();
  const byFirstLetter = new Map<string, string[]>();
  for (const word of rankedWords) {
    if (word.length === 0 || positionOf.has(word)) {
      continue;
    }
    positionOf.set(word, positionOf.size);
    const bucketKey = word[0];
    const bucket = byFirstLetter.get(bucketKey);
    if (bucket) {
      bucket.push(word);
    } else {
      byFirstLetter.set(bucketKey, [word]);
    }
  }

  return {
    size: positionOf.size,
    completionsFor(prefix: string, maxWords: number): string[] {
      if (prefix.length === 0 || maxWords <= 0) {
        return [];
      }
      const bucket = byFirstLetter.get(prefix[0]);
      if (!bucket) {
        return [];
      }
      const typedPosition = positionOf.get(prefix);

      // words that really do begin the way this was typed, commonest first
      const found: string[] = [];
      for (const word of bucket) {
        if (word.startsWith(prefix)) {
          found.push(word);
          if (found.length >= maxWords) {
            return found;
          }
        }
      }

      // Nothing more begins the way it was typed, so allow for it having been MISTYPED. Which
      // test applies depends on whether what was typed is a word at all:
      //
      //  - not a word yet ("broco", "yogrt") -- they are mid-word, so measure to the closest
      //    BEGINNING of a real word: "broco" is one correction from "brocc", so "broccoli".
      //  - already a word ("chiken", "choclate") -- they finished, so measure WHOLE word to
      //    whole word, and only accept something far commoner. Both halves matter: whole-word
      //    distance is what tells "chiken"->"chicken" (one edit) from "chai"->"chicken" (four),
      //    and the commonness gap is what marks the typed word as the misspelling of the two.
      const budget = allowedEdits(prefix);
      if (budget > 0) {
        const corrections: string[] = [];
        for (const word of bucket) {
          if (word.startsWith(prefix)) {
            continue;
          }
          const matches = typedPosition === undefined
            ? isMistypedBeginningOf(prefix, word, budget)
            : wholeWordDistance(prefix, word) <= budget
              && positionOf.get(word)! * CORRECTION_RANK_ADVANTAGE < typedPosition;
          if (matches) {
            corrections.push(word);
          }
        }
        // the correction sharing the most of what was actually typed is the best guess; among
        // equals, the commoner word wins
        corrections.sort((a, b) =>
          sharedBeginningLength(prefix, b) - sharedBeginningLength(prefix, a)
          || positionOf.get(a)! - positionOf.get(b)!);
        for (const word of corrections.slice(0, MAX_CORRECTIONS)) {
          found.push(word);
          if (found.length >= maxWords) {
            break;
          }
        }
      }
      return found;
    },
  };
}

/** Splits text into words the way the dictionary was built; `tokenizeFoodText` in practice. */
export type TokenizeFoodText = (text: string, foldAccents: (text: string) => string) => string[];

/**
 * A character the tokenizer counts as part of a word, rather than as something between words.
 *
 * The two rules below each need to look at ONE character of folded text and ask this, which the
 * tokenizer cannot answer for them -- it hands back whole words, not the reason it cut where it
 * did. So this is the one place the question is written down, and both use it.
 *
 * It mirrors the class `tokenizeFoodText` splits on in `shared/src/food-text-tokenize.ts`. If the
 * two ever disagree, that file is right and this one is wrong: it is the tokenizer the dictionary
 * was built with, and the words on screen came from it.
 */
const WORD_CHARACTER = /[a-z0-9']/;

/**
 * Has the user FINISHED the last word -- ended it with a space, a comma, a bracket?
 *
 * Asked on every keystroke, because a word somebody has ended is offered no completions: there
 * would be nothing at the end of the text for a tap to replace.
 *
 * It reads the FOLDED text, not the raw text, for the same reason `lastWordStartIndex` below
 * does: the folded text is what the tokenizer split, so it is the only text whose last
 * character answers this question the way the tokenizer would. A character that folds away to
 * nothing -- an emoji -- therefore counts as no character at all, and "choc(emoji)" reads as
 * still being inside "choc", which is exactly the word the tokenizer produced for it.
 */
export function lastWordEnded(text: string, foldAccents: (text: string) => string): boolean {
  const folded = foldAccents(text).toLowerCase();
  // text that folds to nothing at all has no word to be inside, so it counts as ended
  return folded.length === 0 || !WORD_CHARACTER.test(folded[folded.length - 1]);
}

/**
 * Where the last word begins in `text`, or -1 when there is no last word at all.
 *
 * This decides which characters a tapped completion overwrites, so it has to agree with
 * `tokenizeFoodText`, which decides what the completions were computed FOR. Agreeing is harder
 * than it looks: the tokenizer folds accents and lowercases BEFORE it splits, and folding is
 * not a character-for-character substitution. One raw character can become several ("寿" folds
 * to "Shou"), or a different one ("’" folds to "'"), or nothing at all.
 *
 * So this does not describe the tokenizer's rule a second time in its own regex -- a duplicate
 * that had to be kept in step by hand, and was not. It ASKS the tokenizer instead: which is the
 * earliest place the text can be cut so that everything after the cut is exactly the one word
 * the completions were offered for?
 *
 * Getting it wrong is not a near miss. The raw-text regex this replaces made "grandma’s" begin
 * its last word at the "s", so a tap produced "grandma’grandmas"; and it began "寿司" at the
 * first character, so a tap replaced a character the offered word had nothing to do with.
 *
 * The answer is -1 only when the text holds no word at all, so a caller that has a completion on
 * screen will always get an index back.
 */
export function lastWordStartIndex(
  text: string,
  foldAccents: (text: string) => string,
  tokenize: TokenizeFoodText,
): number {
  const tokens = tokenize(text, foldAccents);
  if (tokens.length === 0) {
    return -1;
  }
  const lastWord = tokens[tokens.length - 1];
  // The earliest cut after which the text is exactly the last word -- and the cut must leave the
  // word at the very START of what remains, or it swallows whatever sat in front of it.
  // "coca-col" tokenizes the same cut at the hyphen as cut at the "c", and cutting at the hyphen
  // would lose it from the box. Asking that of the FOLDED remainder rather than of the one raw
  // character is what makes a character folding to nothing behave: an Arabic alef pushes no word
  // away from the start, so "الخبز" begins at 0, while an emoji before a space does, so
  // "(emoji) choc" begins at the "c".
  for (let i = 0; i < text.length; i++) {
    const foldedAfterCut = foldAccents(text.slice(i)).toLowerCase();
    if (foldedAfterCut.length === 0 || !WORD_CHARACTER.test(foldedAfterCut[0])) {
      continue;
    }
    const afterCut = tokenize(text.slice(i), foldAccents);
    if (afterCut.length === 1 && afterCut[0] === lastWord) {
      return i;
    }
  }
  // No cut gives the last word on its own, which happens when ONE raw character folds into
  // several words: "½" becomes "1/2", so the offered word came from the second half of a
  // character that cannot be cut in two. Replace the whole of the smallest run that ends with
  // that word -- what the raw-text rule did here, and the best a tap can do.
  let lastRunStart = -1;
  for (let i = 0; i < text.length; i++) {
    const afterCut = tokenize(text.slice(i), foldAccents);
    if (afterCut.length > 0 && afterCut[afterCut.length - 1] === lastWord) {
      lastRunStart = i;
    }
  }
  return lastRunStart;
}

/**
 * Every whole word the unfinished word `typedWord` should be taken to mean.
 *
 * Always contains `typedWord` itself, first, because a word missing from the dictionary must
 * still be searched as typed. Anything after it is a completion, and callers treat those as a
 * weaker kind of match than something the user actually finished typing.
 *
 * `typedWord` must already be lower-cased and accent-folded — i.e. it comes from
 * `tokenizeFoodText`, the same splitting the dictionary itself was built with.
 */
export function expandUnfinishedWord(
  typedWord: string,
  dictionary: FoodWordDictionary,
  maxCompletions: number = MAX_WORD_COMPLETIONS,
): string[] {
  if (typedWord.length === 0) {
    return [];
  }
  const expansion = [typedWord];
  for (const completion of dictionary.completionsFor(typedWord, maxCompletions)) {
    if (completion !== typedWord) {
      expansion.push(completion);
    }
  }
  return expansion;
}

/**
 * What one search actually asks for, once the unfinished word has been completed.
 *
 * `terms` is what the person typed, split and stemmed, exactly as before this feature existed.
 * `lastTermAlternatives` is every whole word that last term may be standing in for — the typed
 * word itself first, then its completions — stemmed the same way, because that is the form the
 * food names are compared in.
 *
 * The alternatives are stemmed AFTER completing, never before, and that order matters: the
 * dictionary holds words a person would recognise ("chocolate"), while matching happens on
 * stems ("chocol"). Completing first and stemming second is what lets the same list be both
 * shown on screen and compared against food names.
 */
export interface CompletedFoodQuery {
  /** every word the person typed, stemmed */
  terms: string[];
  /** whole words the LAST typed word may mean, stemmed, the word as typed first */
  lastTermAlternatives: string[];
  /** the completions as a person would read them, unstemmed, for showing on screen */
  lastWordCompletions: string[];
}

/** The little of the analyzer this needs; the app passes the same object the matcher gets. */
export interface FoodQueryAnalyzerDeps {
  stem: (word: string) => string;
  foldAccents: (text: string) => string;
}

/**
 * Split what somebody typed into words and finish off the last one.
 *
 * Only the LAST word is completed, because it is the only one that can still be half-typed —
 * every earlier word was ended by the space the person typed after it.
 */
export function completeFoodQuery(
  text: string,
  dictionary: FoodWordDictionary,
  deps: FoodQueryAnalyzerDeps,
  tokenize: TokenizeFoodText,
  maxCompletions: number = MAX_WORD_COMPLETIONS,
): CompletedFoodQuery {
  const typedWords = tokenize(text, deps.foldAccents);
  const terms = typedWords.map(word => deps.stem(word)).filter(word => word.length > 0);
  if (typedWords.length === 0) {
    return { terms, lastTermAlternatives: [], lastWordCompletions: [] };
  }
  // A word the user ENDED -- with a space, a comma, a bracket -- is not one they are still
  // typing, so it is offered no completions. Without this, "chocolate " kept offering
  // "chocolates", and worse, those buttons did nothing when tapped: there is no unfinished word
  // at the end of the text for a tap to replace.
  const endedTheLastWord = lastWordEnded(text, deps.foldAccents);
  const expansion = expandUnfinishedWord(
    typedWords[typedWords.length - 1], dictionary, endedTheLastWord ? 0 : maxCompletions);
  const stemmed: string[] = [];
  for (const word of expansion) {
    const stem = deps.stem(word);
    // several completions often share one stem ("chocolate" and "chocolates" both give
    // "chocol"), and comparing the same stem twice would only cost time
    if (stem.length > 0 && !stemmed.includes(stem)) {
      stemmed.push(stem);
    }
  }
  return {
    terms,
    lastTermAlternatives: stemmed,
    // the typed word itself is not a suggestion, so it is not offered back to the person
    lastWordCompletions: expansion.slice(1),
  };
}

